/**
 * Derives what a node card shows at a glance: a human summary of the node's
 * own configuration, and the configuration problems worth flagging on the
 * canvas before the user ever opens the properties panel.
 */

import { unsupportedReason } from '../constants/csmlNodeSupport';

const URL_TYPES = ['image', 'video', 'audio', 'url'];

// 2024-01-07 is a Sunday, so day index 0..6 maps onto sunday..saturday.
const DAY_ORDER = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

function hostOf(url) {
  try {
    return new URL(url).host;
  } catch {
    return url || '';
  }
}

// Rails blob redirect URLs end in the original filename, so the last path
// segment is what the contact will see as the document name.
export function fileNameOf(url) {
  try {
    return decodeURIComponent(new URL(url).pathname.split('/').pop() || '');
  } catch {
    return '';
  }
}

export function fileExtOf(url) {
  const name = fileNameOf(url);
  return name.includes('.') ? name.split('.').pop().toUpperCase() : '';
}

function shortDay(day) {
  const index = DAY_ORDER.indexOf(String(day).toLowerCase());
  if (index < 0) return day;
  return new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(
    new Date(Date.UTC(2024, 0, 7 + index))
  );
}

/**
 * @returns {{ text: string, thumbnail: string, tags: string[] }}
 * `tags` hold raw configuration values (an HTTP verb, a provider name, a
 * host) rather than UI copy, so they are shown verbatim and need no i18n.
 */
export function getNodeSummary(node) {
  const empty = { text: '', thumbnail: '', tags: [] };
  if (!node) return empty;

  switch (node.type) {
    case 'typing':
      return { ...empty, text: `${node.duration || 1000}ms` };

    case 'image':
      return { ...empty, thumbnail: node.content, text: hostOf(node.content) };

    case 'video':
    case 'audio':
      return { ...empty, text: hostOf(node.content) };

    case 'file':
      return {
        ...empty,
        text: fileNameOf(node.content) || hostOf(node.content),
        tags: fileExtOf(node.content) ? [fileExtOf(node.content)] : [],
      };

    case 'url':
      return {
        ...empty,
        text: node.urlText || node.content,
        tags: [hostOf(node.content)].filter(Boolean),
      };

    case 'carousel': {
      const cards = node.cards || [];
      return {
        ...empty,
        thumbnail: cards[0]?.image_url || '',
        text: cards
          .map(card => card.title)
          .filter(Boolean)
          .join(' · '),
      };
    }

    case 'calendar':
      return { ...empty, text: node.content, tags: [node.mode || 'single'] };

    case 'input':
      return {
        ...empty,
        text: node.content,
        tags: [node.inputType || 'text'],
      };

    case 'userinput':
      return { ...empty, text: node.content };

    case 'api':
      return {
        ...empty,
        text: hostOf(node.apiUrl),
        tags: [(node.apiMethod || 'GET').toUpperCase()],
      };

    case 'router':
      return {
        ...empty,
        text: (node.keywords || [])
          .map(rule => rule.match)
          .filter(Boolean)
          .join(' · '),
      };

    case 'assign':
      return {
        ...empty,
        text:
          node.assignType === 'agent'
            ? `Agent #${node.agentId || '?'}`
            : `Team #${node.teamId || '?'}`,
        tags: [],
      };

    case 'oauth':
      return { ...empty, text: node.content, tags: [node.provider] };

    case 'workinghours':
      return {
        ...empty,
        text: `${node.workingStart || '09:00'} – ${node.workingEnd || '17:00'}`,
        tags: [
          node.timezone || 'UTC',
          ...(node.workingDays || []).map(shortDay),
        ],
      };

    default:
      return { ...empty, text: node.content || '' };
  }
}

/**
 * Configuration problems that make a node generate broken or inert CSML.
 * Returns i18n key suffixes under VISUAL_BUILDER.ISSUES.
 */
export function getNodeIssues(node) {
  if (!node) return [];
  const issues = [];

  // A hidden type can still reach the canvas from an existing bot or a
  // pasted script, so say why it will not work rather than leaving the user
  // to discover it through a failed save.
  const unsupported = unsupportedReason(node.type);
  if (unsupported) issues.push(unsupported);

  if (node.type === 'image' && !node.content) {
    issues.push('IMAGE_MISSING');
  } else if (node.type === 'file' && !node.content) {
    issues.push('FILE_MISSING');
  } else if (URL_TYPES.includes(node.type) && !node.content) {
    issues.push('URL_MISSING');
  }
  if (node.type === 'api' && !node.apiUrl) {
    issues.push('API_URL_MISSING');
  }
  if (node.type === 'assign') {
    const target = node.assignType === 'agent' ? node.agentId : node.teamId;
    if (!target) issues.push('ASSIGN_TARGET_MISSING');
  }
  if (['question', 'quickreply'].includes(node.type)) {
    if (!node.buttons?.length) issues.push('NO_BUTTONS');
  }
  if (['radio', 'multiselect', 'checkbox', 'dropdown'].includes(node.type)) {
    if (!node.options?.length) issues.push('NO_OPTIONS');
  }
  if (node.type === 'carousel') {
    if (!node.cards?.length) issues.push('NO_CARDS');
    else if ((node.cards || []).some(card => !card.buttons?.length)) {
      // getOutPorts gives such a card a bare `card<i>` port with nothing to
      // match on, and the generator can only emit a comment for it.
      issues.push('CARD_WITHOUT_BUTTON');
    }
  }
  if (node.type === 'workinghours' && node.enableWorkingHours) {
    if (!node.workingDays?.length) issues.push('NO_WORKING_DAYS');
  }

  return issues;
}
